import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  CloseOrganizationRequest,
  Organization,
  OrganizationDetail,
  SuspendOrganizationRequest,
} from '@nucleus-commerce/api-client/platform';

import { organizationsApi } from '../api/organizations-api';

import { organizationKeys } from './organization-keys';

/** Lifecycle transitions a platform owner or admin can trigger on an organization. */
export type OrganizationAction = 'suspend' | 'reactivate' | 'close' | 'reopen' | 'delete';

/**
 * One mutation per transition. The transition endpoints answer with the bare organization, so a
 * success merges it over the cached detail and invalidates the lists. Close and reopen also
 * refetch the detail: closing pauses the live stores. Delete drops the cached detail instead: the
 * caller leaves the page.
 */
export function useOrganizationActions(organizationId: string) {
  const queryClient = useQueryClient();

  const merge = (organization: Organization) => {
    queryClient.setQueryData<OrganizationDetail>(
      organizationKeys.detail(organizationId),
      (previous) => (previous ? { ...previous, ...organization } : undefined),
    );
    return queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
  };
  const refetchDetail = async (organization: Organization) => {
    await merge(organization);
    await queryClient.invalidateQueries({ queryKey: organizationKeys.detail(organizationId) });
  };

  const suspend = useMutation({
    mutationFn: (input: SuspendOrganizationRequest) =>
      organizationsApi.suspend(organizationId, input),
    meta: { successMessage: 'organizations:toasts.suspended' },
    onSuccess: merge,
  });
  const reactivate = useMutation({
    mutationFn: () => organizationsApi.reactivate(organizationId),
    meta: { successMessage: 'organizations:toasts.reactivated' },
    onSuccess: merge,
  });
  // The dialog shows OPEN_ORDERS next to the force switch instead of a toast.
  const close = useMutation({
    mutationFn: (input: CloseOrganizationRequest) => organizationsApi.close(organizationId, input),
    meta: { successMessage: 'organizations:toasts.closed', silentError: true },
    onSuccess: refetchDetail,
  });
  const reopen = useMutation({
    mutationFn: () => organizationsApi.reopen(organizationId),
    meta: { successMessage: 'organizations:toasts.reopened' },
    onSuccess: refetchDetail,
  });

  const remove = useMutation({
    mutationFn: () => organizationsApi.remove(organizationId),
    meta: { successMessage: 'organizations:toasts.deleted' },
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: organizationKeys.detail(organizationId) });
      await queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });

  return { suspend, reactivate, close, reopen, delete: remove };
}
